import * as React from 'react';

type TimeoutRange = number | [number, number];

const MINIMUM_THRESHOLD: number = 30;

const randomizeTimeout: any = (ms: TimeoutRange): number =>
  Array.isArray(ms)
    ? // random value inside the specified min and max thresholds
      ms[0] + Math.random() * (ms[1] - ms[0])
    : // randomize the value - with a minimum threshold
      Math.max(Math.random() * ms, MINIMUM_THRESHOLD);

enum Tick {
  INIT,
  WRITE,
  DELETE,
  START_DELETE
}

enum DEFAULTS {
  WRITE_SPEED_MS = 100,
  DELETE_SPEED_MS = 60,
  WAIT_BEFORE_DELETE_MS = 9000
}

export interface RenderArgs {
  currentText: string;
  fullCurrentText: string;
}

interface Props {
  strings: string[];
  waitBeforeDeleteMs?: number;
  writeSpeedMs?: TimeoutRange;
  deleteSpeedMs?: TimeoutRange;
  children: (args: RenderArgs) => React.ReactNode;
}

interface State {
  currentStringIdx: number;
  currentCharPos: number;
  isDeleting: boolean;
}

const moveToNextString: any = (
  prevState: State,
  props: Props
): Pick<State, 'isDeleting' | 'currentCharPos' | 'currentStringIdx'> => {
  const nextStringIdx: number = prevState.currentStringIdx + 1;

  return {
    isDeleting: false,
    currentCharPos: 0,
    currentStringIdx: nextStringIdx < props.strings.length ? nextStringIdx : 0
  };
};

const moveCharPos: any = (change: number): any => (
  prevState: State
): Pick<State, 'currentCharPos'> => ({
  currentCharPos: prevState.currentCharPos + change
});

const startDeleting: any = (): Pick<State, 'isDeleting'> => ({
  isDeleting: true
});

class Typewriting extends React.PureComponent<Props, State> {
  private tickTimeout: number | null = null;

  constructor(props: Props) {
    super(props);
    this.state = {
      currentStringIdx: 0,
      currentCharPos: 0,
      isDeleting: false
    };
  }

  public componentDidMount(): void {
    this.queueTick(Tick.INIT);
  }

  public componentWillUnmount(): void {
    if (this.tickTimeout !== null) {
      clearTimeout(this.tickTimeout);
    }
  }

  public render(): JSX.Element {
    const { strings } = this.props;
    const { currentStringIdx, currentCharPos } = this.state;

    const fullCurrentText: string = strings[currentStringIdx];
    const currentText: string = fullCurrentText.slice(0, currentCharPos);

    return this.props.children({ currentText, fullCurrentText }) as any;
  }

  private queueTick(tickType: Tick): void {
    const { writeSpeedMs, deleteSpeedMs, waitBeforeDeleteMs } = this.props;

    const timeout: number =
      tickType === Tick.INIT
        ? 0
        : tickType === Tick.WRITE
        ? randomizeTimeout(writeSpeedMs !== null ? writeSpeedMs : DEFAULTS.WRITE_SPEED_MS)
        : tickType === Tick.DELETE
        ? randomizeTimeout(deleteSpeedMs !== null ? deleteSpeedMs : DEFAULTS.DELETE_SPEED_MS)
        : tickType === Tick.START_DELETE
        ? waitBeforeDeleteMs !== null
          ? waitBeforeDeleteMs
          : DEFAULTS.WAIT_BEFORE_DELETE_MS
        : 0; // ¯\_(ツ)_/¯

    this.tickTimeout = window.setTimeout(() => this.tick(), timeout);
  }

  private tick(): void {
    const { currentStringIdx, currentCharPos, isDeleting } = this.state;
    const currentText: string = this.props.strings[currentStringIdx];

    if (!isDeleting) {
      if (currentCharPos >= currentText.length) {
        this.setState(startDeleting, () => this.queueTick(Tick.START_DELETE));
      } else {
        this.setState(moveCharPos(1), () => this.queueTick(Tick.WRITE));
      }
    } else {
      if (currentCharPos <= 0) {
        this.setState(moveToNextString, () => this.queueTick(Tick.WRITE));
      } else {
        this.setState(moveCharPos(-1), () => this.queueTick(Tick.DELETE));
      }
    }
  }
}

// for backwards compatibility
export { Typewriting };
